import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {(event: Event) => void} */
  #handleDocumentClick = (event) => {
    if (!this.dataset.inlineSizes || !this.hasAttribute('data-sizes-open')) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#setPanelOpen(false);
  };

  /** @type {(event: KeyboardEvent) => void} */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.dataset.inlineSizes || !this.hasAttribute('data-sizes-open')) return;
    event.stopPropagation();
    this.#setPanelOpen(false);
    this.querySelector('.quick-add__button--choose')?.focus({ preventScroll: true });
  };

  /**
   * Opens or closes the inline size panel, syncing aria attributes.
   * @param {boolean} open
   */
  #setPanelOpen(open) {
    const panel = /** @type {HTMLElement | null} */ (this.querySelector('.quick-add__sizes-panel'));
    const trigger = /** @type {HTMLElement | null} */ (this.querySelector('.quick-add__button--choose'));

    if (open) {
      this.setAttribute('data-sizes-open', '');
      panel?.setAttribute('aria-hidden', 'false');
      trigger?.setAttribute('aria-expanded', 'true');
      const firstRow = /** @type {HTMLElement | null} */ (this.querySelector('.quick-add__size-row:not([disabled])'));
      firstRow?.focus({ preventScroll: true });
    } else {
      this.removeAttribute('data-sizes-open');
      panel?.setAttribute('aria-hidden', 'true');
      trigger?.setAttribute('aria-expanded', 'false');
    }
  }

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeydown);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click.
   * In inline-sizes mode (cart drawer), toggles the inline size panel instead of opening the modal.
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Inline-sizes mode: reveal the size panel instead of opening the modal
    if (this.dataset.inlineSizes) {
      const isOpen = this.hasAttribute('data-sizes-open');
      this.#setPanelOpen(!isOpen);
      return;
    }

    try {
      const currentUrl = this.productPageUrl;

      if (!currentUrl) {
        console.warn('[quick-add] No product page URL found — cannot open modal.');
        return;
      }

      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          } else {
            console.warn('[quick-add] Fetched page does not contain [data-product-grid-content]:', currentUrl);
          }
        }
        // html is null when the fetch was aborted — not an error, just bail silently.
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#openQuickAddModal();
      }
    } catch (error) {
      console.error('[quick-add] Error opening quick-add modal:', error);
    }
  };

  /**
   * Handles a size chip click in inline-sizes mode.
   * Finds the matching variant (respecting any currently selected swatch/color),
   * updates the hidden variant input, then submits the form.
   * @param {Event} event - The click event
   */
  handleSizeClick = (event) => {
    const chip = /** @type {HTMLElement | null} */ (
      event.target instanceof HTMLElement ? event.target.closest('.quick-add__size-row, .quick-add__size-chip') : null
    );
    if (!chip) return;

    const sizeValue = chip.dataset.sizeValue;
    const sizeOptionPos = parseInt(chip.dataset.optionPosition || '0'); // 1-indexed
    if (!sizeValue || !sizeOptionPos) return;

    const variantsScript = this.querySelector('[data-inline-variants]');
    if (!variantsScript) return;

    /** @type {{ id: number, available: boolean, options: string[] }[]} */
    const variants = JSON.parse(variantsScript.textContent || '[]');

    // Build a map of currently selected option values from checked radio inputs in the card
    // (covers swatch selections made before clicking Add)
    const productCard = this.closest('product-card');
    /** @type {Record<number, string>} */
    const selectedOptions = {};

    const checkedInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      productCard?.querySelectorAll('input[type="radio"]:checked[data-input-id]') || []
    );

    for (const input of checkedInputs) {
      const parts = input.dataset.inputId?.split('-');
      if (parts?.[0]) {
        const pos = parseInt(parts[0]); // 1-indexed
        selectedOptions[pos] = input.value;
      }
    }

    // Override with the explicitly clicked size
    selectedOptions[sizeOptionPos] = sizeValue;

    // Find a matching available variant (all selected options must match)
    const posCount = Object.keys(selectedOptions).length;
    let match = variants.find((v) => {
      if (!v.available) return false;
      return Object.entries(selectedOptions).every(([pos, val]) => v.options[parseInt(pos) - 1] === val);
    });

    // Fallback: if no exact match (e.g. selected swatch has no stock in this size),
    // find the first available variant with this size regardless of other options
    let usedFallback = false;
    if (!match && posCount > 1) {
      match = variants.find((v) => v.available && v.options[sizeOptionPos - 1] === sizeValue);
      usedFallback = Boolean(match);
    }

    if (!match) return;

    // Update the hidden variant input
    const variantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (variantInput) variantInput.value = String(match.id);

    // If we fell back to a different combination (e.g. user had Red selected
    // but this size is only available in Blue), sync the swatch radios so the
    // product card visually reflects the variant that will actually be added.
    if (usedFallback && productCard) {
      this.#syncSwatchesToVariant(productCard, match.options, sizeOptionPos);
    }

    // Close the panel before submitting
    this.#setPanelOpen(false);

    // Submit via the form's native requestSubmit so product-form-component handleSubmit fires
    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('form'));
    form?.requestSubmit?.();
  };

  /**
   * Sync checked radio inputs in the product card to match the given variant's
   * options, skipping the size option (which lives in the inline panel, not as
   * a radio in the card). Fires `change` so any swatch-driven UI updates.
   * @param {Element} productCard
   * @param {string[]} variantOptions
   * @param {number} sizeOptionPos - 1-indexed position of the size option to skip
   */
  #syncSwatchesToVariant(productCard, variantOptions, sizeOptionPos) {
    variantOptions.forEach((value, index) => {
      const pos = index + 1; // 1-indexed
      if (pos === sizeOptionPos) return;

      const input = /** @type {HTMLInputElement | null} */ (
        productCard.querySelector(`input[type="radio"][data-input-id^="${pos}-"][value="${CSS.escape(value)}"]`)
      );
      if (!input || input.checked) return;

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /**
   * Closes the inline size panel and returns focus to the trigger button.
   */
  handleCloseClick = () => {
    this.#setPanelOpen(false);
    this.querySelector('.quick-add__button--choose')?.focus({ preventScroll: true });
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const media = productGrid.querySelector('.product-information__media');
    if (media) media.remove();

    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');

    if (productDetails && productFormComponent && variantPicker && productPrice) {
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
